import * as React from 'react';
import { cn } from '../cn';

export interface Column<T> {
  key: string;
  label: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
  className?: string;
  render?: (row: T, index: number) => React.ReactNode;
  /** hide on small screens */
  hideBelow?: 'sm' | 'md' | 'lg';
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  /** Renders each row as a link (server-safe). */
  rowHref?: (row: T) => string | undefined;
  /** Client-side click handler; pass from a client component only. */
  onRowClick?: (row: T) => void;
  density?: 'compact' | 'regular';
  emptyState?: React.ReactNode;
  className?: string;
  stickyHeader?: boolean;
  /** Renders inside a Card-like frame (default true). */
  framed?: boolean;
  footer?: React.ReactNode;
  selectedKey?: string;
  linkComponent?: React.ComponentType<{ href: string; className?: string; children?: React.ReactNode; 'aria-label'?: string }>;
}

const hide = { sm: 'max-sm:hidden', md: 'max-md:hidden', lg: 'max-lg:hidden' };

export function DataTable<T>({ columns, rows, rowKey, rowHref, onRowClick, density = 'regular', emptyState, className, stickyHeader = true, framed = true, footer, selectedKey, linkComponent }: DataTableProps<T>) {
  const pad = density === 'compact' ? 'px-3 py-1.5' : 'px-4 py-2.5';
  const Link = linkComponent ?? (({ href, className, children, ...rest }) => (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  ));
  return (
    <div className={cn(framed && 'bg-surface rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] overflow-hidden', className)}>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[13.5px]">
          <thead className={cn(stickyHeader && 'sticky top-0 z-10', 'bg-surface-2')}>
            <tr className="hairline-b">
              {columns.map((c) => (
                <th key={c.key} scope="col" style={{ width: c.width }} className={cn(pad, 'text-[12px] font-medium text-text-2 text-left whitespace-nowrap select-none', c.align === 'right' && 'text-right', c.align === 'center' && 'text-center', c.hideBelow && hide[c.hideBelow])}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-text-3 text-[13px]">
                  {emptyState ?? 'Nothing here yet.'}
                </td>
              </tr>
            ) : (
              rows.map((row, i) => {
                const key = rowKey(row, i);
                const href = rowHref?.(row);
                const clickable = !!href || !!onRowClick;
                return (
                  <tr key={key} onClick={onRowClick ? () => onRowClick(row) : undefined} aria-selected={selectedKey === key || undefined} className={cn('hairline-b last:shadow-none transition-colors', clickable && 'cursor-pointer hover:bg-fill-3', selectedKey === key && 'bg-accent-tint hover:bg-accent-tint')}>
                    {columns.map((c, ci) => {
                      const content = c.render ? c.render(row, i) : ((row as Record<string, unknown>)[c.key] as React.ReactNode);
                      return (
                        <td key={c.key} className={cn(pad, 'align-middle text-text', c.align === 'right' && 'text-right tabular', c.align === 'center' && 'text-center', c.hideBelow && hide[c.hideBelow], c.className, ci === 0 && href && 'relative')}>
                          {ci === 0 && href ? (
                            <Link href={href} className="after:absolute after:inset-0 after:content-['']">
                              {content}
                            </Link>
                          ) : (
                            content
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {footer ? <div className="px-4 py-2.5 hairline-t text-[12px] text-text-2 flex items-center justify-between gap-3">{footer}</div> : null}
    </div>
  );
}
